import { $inject, $type } from '../utils/di-container.js';
import { LoggerService } from './logger.service.js';
import { FeatureService } from './feature.service.js';
import { MetadataService } from "./metadata.service";
import { ExternalControlService } from "./external-control.service";

/**
 * Service accessible via window.authorizedby.badge2.api, i.e. helps enabling debug.
 */
export class GlobalService {

  /** @type {LoggerService} */
  _logger;

  /** @type {MetadataService} */
  _metadataService;

  /** @type {BadgeService} */
  _badgeService;

  /** @type {FeatureService} */
  _featureService;

  /** @type {function} */
  _badge2Boot;

  /** @type {ExternalControlService} */
  _control;

  /** @type {boolean} */
  externalControl = false;

  /** @type {boolean} */
  verbose = false;

  /** @type {boolean} */
  keepJournal = false;

  /** @type {boolean} */
  consoleLogs = false;

  [$inject](obtain) {
    this._logger = obtain(LoggerService);
    this._featureService = obtain(FeatureService);
    this._metadataService = obtain(MetadataService);
    this._control = obtain(ExternalControlService);

    // only needed for debug, and we must avoid static loading because the badges_lib build breaks that way
    import('./badge.service.js').then(({ BadgeService }) => this._badgeService = obtain(BadgeService));
    import('../badge2.boot.js').then(({ badge2Boot }) => this._badge2Boot = badge2Boot);
  }

  init() {
    if ((window.authorizedby || {}).badge2) {
      throw new Error('Badge2 already loaded.');
    }

    if (!window.authorizedby) {
      window.authorizedby = {};
    }

    if (!window.authorizedby.badge2) {
      window.authorizedby.badge2 = {};
    }

    window.authorizedby.badge2.info = {
      env: process.env.NODE_ENV,
      version: process.env.VERSION,
      gitRev: process.env.GIT_REVISION,
    };

    window.authorizedby.badge2.api = this;

    document.dispatchEvent(new CustomEvent('authorizedby_badge_initialized'));
  }

  enableLogs() {
    this.consoleLogs = true;
    return this;
  }

  /**
   * When enabled, a log journal will be kept - which contains structured info that can be parsed by external agents.
   */
  enableJournaling() {
    this.keepJournal = true;
    return this;
  }

  /**
   * When enabled, more logs will be printed.
   */
  enableVerbose() {
    this.verbose = true;
    return this;
  }

  /**
   * Enables debug for all or specific variants of badges.
   * @param {_BadgeVariant} [variant]
   * @param {string} [debug]
   */
  debugBadge(variant, debug = 'composition.execution') {
    const badges = this._badgeService.getBadges({ activeOnly: true, variant });
    badges.forEach(badge => badge.setDebug(debug));
    return this;
  }

  /**
   * Shows enabled features
   */
  showFeatures() {
    this._logger.debug('debug/enabled-features', `Enabled features:\n${this._featureService.features.sort().join('\n')}`, () => ({ enabledFeatures: this._featureService.features.slice() }));
    return this;
  }

  /**
   * @param {string} feature
   */
  enableFeature(feature) {
    this._featureService.enable(feature);
    this._badgeService.refreshAll('feature-change');
    this.showFeatures();
    return this;
  }

  /**
   * @param {string} feature
   */
  disableFeature(feature) {
    this._featureService.disable(feature);
    this._badgeService.refreshAll('feature-change');
    this.showFeatures();
    return this;
  }

  enableExternalControl() {
    this.externalControl = true;
    return this;
  }

  async forceRefresh() {
    await this._metadataService._checkMetadataChanges(true);
    this._badgeService.refreshAll('requested');
  }

  reload() {
    delete window.authorizedby.badge2;
    this._badge2Boot();
  }

}

GlobalService[$type] = Symbol('GlobalService');
